/**
 * ProceduralNoise — helpers that paint noise-based bump and roughness
 * textures into DynamicTexture surfaces so procedural PBR materials
 * (ground, platforms, floors) stop looking flat-shaded under moving
 * lights.
 *
 * No asset file needed — textures are drawn at scene-init and cached on
 * the scene via a small registry so repeat callers reuse a single
 * texture instead of minting per material.
 *
 * Usage:
 *   const bump = getOrMakeNoiseBump(scene, "atriumFloor", 0.6);
 *   mat.bumpTexture = bump;
 *   mat.bumpTexture.level = 0.35;   // how strongly it wrinkles the surface
 */
import { Scene } from "@babylonjs/core/scene";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";

interface NoiseRegistry {
  [key: string]: Texture;
}

function getRegistry(scene: Scene): NoiseRegistry {
  const holder = scene as unknown as { __proceduralNoiseRegistry?: NoiseRegistry };
  if (!holder.__proceduralNoiseRegistry) {
    holder.__proceduralNoiseRegistry = {};
  }
  return holder.__proceduralNoiseRegistry;
}

/**
 * Deterministic 32-bit LCG — gives the same noise every load so visual
 * regression tests stay stable.
 */
function makeRand(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return (s >>> 0) / 4294967296;
  };
}

/**
 * Paint a fractional Brownian motion-ish noise into the dynamic texture.
 * Each "octave" is a stamped grid of random cells blurred by subsequent
 * passes. Output is a grayscale texture suitable for bump/roughness.
 */
function paintFbm(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  octaves: number,
  seed: number,
  contrast: number,
): void {
  const rand = makeRand(seed);
  ctx.fillStyle = "#808080";
  ctx.fillRect(0, 0, w, h);

  for (let o = 0; o < octaves; o++) {
    const cell = Math.max(4, Math.floor(w / (8 << o)));
    const amp = 0.5 / (o + 1);
    for (let y = 0; y < h; y += cell) {
      for (let x = 0; x < w; x += cell) {
        const v = rand();
        const g = Math.max(0, Math.min(255, Math.round(128 + (v - 0.5) * 2 * 128 * amp * contrast)));
        ctx.globalAlpha = 0.45;
        ctx.fillStyle = `rgb(${g},${g},${g})`;
        ctx.fillRect(x, y, cell + 1, cell + 1);
      }
    }
  }
  ctx.globalAlpha = 1;

  // Soften the grid stamps with a box-blur pass. Canvas doesn't expose
  // a native blur filter we can rely on everywhere; approximate with a
  // semi-transparent redraw at a 1-pixel offset in each direction.
  const original = ctx.getImageData(0, 0, w, h);
  for (let pass = 0; pass < 2; pass++) {
    ctx.putImageData(original, 0, 0);
    ctx.globalAlpha = 0.33;
    ctx.drawImage(ctx.canvas, 1, 0);
    ctx.drawImage(ctx.canvas, -1, 0);
    ctx.drawImage(ctx.canvas, 0, 1);
    ctx.drawImage(ctx.canvas, 0, -1);
  }
  ctx.globalAlpha = 1;
}

/**
 * Get or create a cached grayscale noise texture tagged by key. The key
 * is also the seed so levels can request named variations and get
 * distinct-but-stable noise.
 */
export function getOrMakeNoiseBump(
  scene: Scene,
  key: string,
  contrast = 0.6,
  size = 512,
): Texture {
  const reg = getRegistry(scene);
  if (reg[key]) return reg[key];

  const seed = hashSeed(key);
  const dt = new DynamicTexture(
    `procedural_noise_${key}`,
    { width: size, height: size },
    scene,
    true,
  );
  const ctx = dt.getContext() as unknown as CanvasRenderingContext2D;
  paintFbm(ctx, size, size, 4, seed, contrast);
  dt.update(false);
  dt.wrapU = Texture.WRAP_ADDRESSMODE;
  dt.wrapV = Texture.WRAP_ADDRESSMODE;
  reg[key] = dt;
  return dt;
}

function hashSeed(s: string): number {
  let h = 0x811c9dc5 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}
